/**
 * 2D art for the UI: the HQ icon set (`art/hq/icons`, 256² transparent renders of the same models — every tower tier,
 * base tier, enemy, boss, troop, path card, resource and chest) with the interface portraits as the fallback for the
 * few ids the icon set lacks. URLs go through the asset library's inlined store so single-file builds resolve them.
 */
import { AssetLibrary } from "../render/assets";
import { ENEMY_STAND_IN, enemyArtId } from "../render/views/enemyStandIns";

const TOWER_IDS = new Set(["archer", "cannon", "frost", "fire", "lightning", "ballista", "poison", "beacon", "mortar", "barracks", "arcane", "hex"]);
const ENEMY_IDS = new Set(["grunt", "runner", "brute", "shieldbearer", "healer", "flyer", "bomber"]);
const PORTRAITS: Record<string, string> = { rally_flag: "rally_flag" };

/** HQ icon name for a game id (tower/base at a tier, enemy, boss, troop), or null when the set has none. */
export function hqIconName(id: string, tier = 1): string | null {
  const t = Math.max(1, Math.min(3, tier));
  if (TOWER_IDS.has(id)) return `tower_${id}_t${t}`;
  if (id === "base") return `base_t${t}`;
  if (id in ENEMY_STAND_IN) { const art = enemyArtId(id); return art === "grunt" ? "enemy_raider" : art.startsWith("boss_") ? art : `enemy_${art}`; }   // ENEMIES_V2: a stand-in's icon until its own ships
  if (id === "grunt") return "enemy_raider";
  if (ENEMY_IDS.has(id)) return `enemy_${id}`;
  if (id.startsWith("boss_")) return id;
  if (/^rare_c\d$/.test(id)) return `enemy_${id}`;                // M36: ART's own rare icons (hq/icons/enemy_rare_cN.png)
  if (id === "wyrm") return "enemy_wyrm";                          // M40: the Starfall Wyrm (hq/icons/enemy_wyrm.png)
  if (id === "troop") return "troop_soldier";
  if (id.startsWith("chest_")) return id;
  return null;
}

export function portraitUrl(id: string, tier = 1): string | null {
  const hq = hqIconName(id, tier);
  if (hq) return AssetLibrary.resolveInlined(`./hq/icons/${hq}.png`);
  const name = PORTRAITS[id];
  return name ? AssetLibrary.resolveInlined(`./interface/portraits/${name}.png`) : null;
}

/** Phase 3 card portrait: a heroic up-shot of the tower's top (`hq/icons/card_<tower>_t<n>.png`, 256x320, art2d). */
export function cardArtUrl(towerId: string, tier = 1): string | null {
  if (!TOWER_IDS.has(towerId)) return null;
  return AssetLibrary.resolveInlined(`./hq/icons/card_${towerId}_t${Math.max(1, Math.min(3, tier))}.png`);
}

/** Phase 3 archetype-tinted card background (`interface/cards/cardback_<tower>.jpg`, 320x400, art2d). */
export function cardBackUrl(towerId: string): string | null {
  return TOWER_IDS.has(towerId) ? uiUrl(`cards/cardback_${towerId}.jpg`) : null;
}

/**
 * A tower card's art: the up-shot portrait over its archetype backdrop. Falls back to the old whole-tower portrait
 * (and the CSS gradient) when the Phase 3 files are missing, so an ungrafted build still shows a tower.
 */
export function towerCardArt(towerId: string, height: number, fallbackHtml: string, tier = 1): { art: string; back: string } {
  const hero = cardArtUrl(towerId, tier), back = cardBackUrl(towerId), old = portraitUrl(towerId, tier);
  const onerr = old ? ` onerror="this.onerror=null;this.classList.remove('hero');this.src='${old}'"` : "";
  const art = hero ? `<img class="hero" decoding="sync" src="${hero}"${onerr} alt="" style="height:${height}px;display:block">` : portraitImg(towerId, height, fallbackHtml);
  return { art, back: back ? `<div class="cback" style="background-image:url('${back}')"></div>` : "" };
}

/**
 * Tier 4 (M21, docs/TOWERS_V2.md "Tier 4"): a branch's icon and card art, `hq/icons/tower_<tower>_t4_<branch>.png` and
 * `hq/icons/card_<tower>_t4_<branch>.png`, from the ascend-art agents. Until they land (or in a build without them) an
 * `<img>` falls back on error to the tower's tier-3 art, and `.iv` frames it in gold with a "IV" plate (hand.css), so the
 * same markup lights up the moment the files ship.
 */
export function t4IconUrl(towerId: string, branchId: string): string { return AssetLibrary.resolveInlined(`./hq/icons/tower_${towerId}_t4_${branchId}.png`); }
export function t4CardArtUrl(towerId: string, branchId: string): string { return AssetLibrary.resolveInlined(`./hq/icons/card_${towerId}_t4_${branchId}.png`); }

/**
 * A tier-4 portrait: the branch's own card art (or icon) when `branchId` is given and the file loads, else the tier-3 card
 * art (else portrait), always inside the gold "IV" frame. `kind` picks the card art (a hero up-shot) or the icon (a whole
 * tower).
 */
export function tierFourArt(towerId: string, branchId: string | null, height: number, kind: "card" | "icon" = "card"): string {
  const t3 = kind === "card" ? cardArtUrl(towerId, 3) : portraitUrl(towerId, 3);
  const own = branchId === null ? null : kind === "card" ? t4CardArtUrl(towerId, branchId) : t4IconUrl(towerId, branchId);
  const src = own ?? t3 ?? "";
  const fallback = own !== null && t3 ? ` onerror="this.onerror=null;this.src='${t3}';this.parentElement.classList.add('t3')"` : "";
  return `<span class="iv${own === null ? " t3" : ""}"><img class="${kind === "card" ? "hero" : "port"}" decoding="sync" src="${src}"${fallback} alt="" style="height:${height}px;display:block"><i class="ivplate disp">IV</i></span>`;
}

/** Path-card art by shape id (`i2`, `l3`, `mud_i3`, …). */
export function pathIconUrl(shapeId: string): string { return AssetLibrary.resolveInlined(`./hq/icons/path_${shapeId}.png`); }

/** Resource icon (`gold`, `gems`, `speedup`, `points`, `cards`). */
export function resourceIconUrl(id: string): string { return AssetLibrary.resolveInlined(`./hq/icons/res_${id}.png`); }

/** Chest render per rarity id ("common"…"mythic") from the HQ icon set. */
export function chestIconUrl(rarityId: string): string { return AssetLibrary.resolveInlined(`./hq/icons/chest_${rarityId}.png`); }

export function portraitImg(id: string, height: number, fallbackHtml: string, extraStyle = "", tier = 1): string {
  const url = portraitUrl(id, tier);
  return url ? `<img decoding="sync" src="${url}" alt="" style="height:${height}px;display:block;filter:drop-shadow(0 4px 4px rgba(0,0,0,0.5));${extraStyle}">` : fallbackHtml;
}

/** Any file under `art/interface/` (drop 2: title, sky, chests, gear, empty states, chapters, captain). */
export function uiUrl(rel: string): string { return AssetLibrary.resolveInlined(`./interface/${rel}`); }

/** Painted gear icon per slot and rarity (`art/interface/gear/gear_<slot>_<rarity>.png`). */
export function gearIconUrl(slot: string, rarityId: string): string { return uiUrl(`gear/gear_${slot}_${rarityId}.png`); }
